import { useState, useMemo } from 'react';
import { EXERCISE_DATABASE, EXERCISE_MAP } from '../constants/exerciseDatabase';
import { s } from '../constants/styles';
import { todayStr, todayDow } from '../utils/date';
import { getDayMeta, getDayExercises, getConsecutiveWeeks, getOverloadSuggestion, findLastSession, getNextTrainingDay, getAggregateMuscleDiagram, getExerciseHistory } from '../utils/workout';
import ExercisePicker from './ExercisePicker';
import MuscleDiagram from './MuscleDiagram';
import VideoModal from './VideoModal';

export default function WorkoutView({ data, activeDow, onStartExercise, onSkip, onSaveNotes, onSaveCardio, onRemoveCardio, onSwap, onRestoreSwap }) {
  const today = todayStr();
  const dayPlan = getDayMeta(activeDow, data.dayStructure);
  const exercises = getDayExercises(activeDow, data.customProgram, data.dayStructure);
  const isRest = exercises.length === 0;
  const session = data.sessions[today];
  const lastSess = findLastSession(data.sessions, activeDow, today);
  const weeks = getConsecutiveWeeks(data.sessions);
  const [notes, setNotes] = useState(session?.notes || "");
  const [swapPickerFor, setSwapPickerFor] = useState(null);
  const [videoModalFor, setVideoModalFor] = useState(null); // { videoId, name }

  function resolveReplacementEx(replacementId, replacementName) {
    return EXERCISE_MAP.get(replacementId) || {
      id: replacementId, name: replacementName,
      sets: 3, rLo: 8, rHi: 12, compound: false, inc: 2.5,
    };
  }
  const [cardio, setCardio] = useState({ type: "Treadmill", duration: 30, incline: 12, speed: 3.5, notes: "" });
  const [showCardioForm, setShowCardioForm] = useState(false);
  const [cardioSaved, setCardioSaved] = useState(false);
  const cardioEntries = Array.isArray(session?.cardio) ? session.cardio : session?.cardio ? [session.cardio] : [];
  const isToday = activeDow === todayDow();

  const muscleFocus = useMemo(() => {
    if (exercises.length === 0) return null;
    const effective = exercises.map(ex => {
      const exD = session?.exercises?.[ex.id];
      if (exD?.swapped && exD?.replacedBy) return resolveReplacementEx(exD.replacedBy, exD.replacedByName);
      return ex;
    });
    const { primary, secondary } = getAggregateMuscleDiagram(effective);
    if (primary.length === 0 && secondary.length === 0) return null;
    return { primary, secondary };
  }, [exercises, session]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isRest) {
    const next = getNextTrainingDay(activeDow, data.customProgram, data.dayStructure);
    return (
      <div className="fade-in" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", padding: 24 }}>
        <div style={{ width: 88, height: 88, borderRadius: "50%", background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.2)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
          <svg width="62" height="62" viewBox="0 0 100 105" fill="none">
            {/* back log */}
            <line x1="12" y1="70" x2="88" y2="95" stroke="#92400e" strokeWidth="11" strokeLinecap="round"/>
            {/* flames */}
            <path d="M50 78 C38 72 22 56 28 34 C32 46 40 46 38 36 C44 48 46 66 50 78Z" fill="#fb923c"/>
            <path d="M50 78 C62 72 78 56 72 34 C68 46 60 46 62 36 C56 48 54 66 50 78Z" fill="#fb923c"/>
            <path d="M50 78 C36 60 34 32 43 12 C44 28 50 30 49 18 C56 30 64 58 50 78Z" fill="#f59e0b"/>
            <path d="M50 72 C46 61 45 46 49 30 C50 42 54 46 50 72Z" fill="#fdba74"/>
            {/* front log */}
            <line x1="88" y1="70" x2="12" y2="95" stroke="#b45309" strokeWidth="11" strokeLinecap="round"/>
          </svg>
        </div>
        <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Rest Day</h2>
        <p style={{ color: "var(--text-3)", fontSize: 14, marginBottom: 24 }}>{dayPlan.day}</p>
        {next && (
          <div style={{ ...s.card, ...s.cardGlow, padding: 20, textAlign: "center", width: "100%" }}>
            <div style={{ ...s.label, marginBottom: 4 }}>NEXT SESSION</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: next.color || "var(--accent)" }}>{next.day} — {next.name}</div>
            <div style={{ fontSize: 13, color: "var(--text-3)", marginTop: 4 }}>{next.exercises.length} exercises</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ paddingBottom: 100 }}>
      <div style={{ padding: "0 20px 12px" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-3)" }}>
          {isToday ? new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : dayPlan.day + " Preview"}
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: dayPlan.color || "var(--text-1)" }}>{dayPlan.name}</h1>
        {!isToday && <div style={{ fontSize: 12, color: "var(--amber)", fontWeight: 600, marginTop: 2 }}>Viewing only. Log on the actual day.</div>}
      </div>

      {/* Today's Focus — aggregate muscle diagram, using effective exercises after swaps */}
      {muscleFocus && (
        <div style={{ margin: "0 16px 12px", padding: "12px 16px 10px", borderRadius: 16, background: "var(--bg-2)", border: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ ...s.label, marginBottom: 8 }}>TODAY'S FOCUS</div>
          <MuscleDiagram primary={muscleFocus.primary} secondary={muscleFocus.secondary} color={dayPlan.color || "#4f8fff"} size="compact" />
        </div>
      )}

      {weeks >= 4 && isToday && (
        <div style={{ margin: "0 20px 10px", padding: "10px 14px", borderRadius: 12, background: "var(--amber-dim)", border: "1px solid rgba(251,191,36,0.12)" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--amber)" }}>Week {weeks}. Consider a deload if fatigue is building.</span>
        </div>
      )}

      <div style={{ padding: "0 16px" }}>
        {exercises.map(ex => {
          const exD = session?.exercises?.[ex.id];
          const isSwapped = exD?.swapped === true;
          const repLabel = ex.perLeg ? `${ex.rLo}/leg` : `${ex.rLo}${ex.rHi !== ex.rLo ? "-" + ex.rHi : ""}`;
          const skipped = exD?.skipped;

          // Resolve display exercise — replacement if swapped, original if not
          const displayEx = isSwapped ? resolveReplacementEx(exD.replacedBy, exD.replacedByName) : ex;
          const displayExD = isSwapped ? session?.exercises?.[exD.replacedBy] : exD;
          const displaySets = displayExD?.sets || [];
          const ws = displaySets.filter(s => !s.warmup);
          const done = ws.length >= ex.sets;
          const hasPR = displaySets.some(s => s.pr);

          // Previous session data — use replacement history if swapped
          const lastEx = isSwapped
            ? getExerciseHistory(data.sessions, displayEx.id, 1)[0]
            : lastSess?.exercises?.[ex.id];
          const lastW = isSwapped
            ? (lastEx?.sets || []).filter(s => !s.warmup)
            : (lastEx?.sets || []).filter(s => !s.warmup);

          // Suggestion — based on whichever exercise is actually being done
          const sug = getOverloadSuggestion(displayEx, data.sessions, data.startingWeights);

          // All-time PR for display exercise
          let allTimePR = null;
          for (const [, sess] of Object.entries(data.sessions)) {
            const sts = sess.exercises?.[displayEx.id]?.sets || [];
            for (const st of sts) {
              if (!st.warmup && st.weight > 0) {
                if (!allTimePR || st.weight > allTimePR.weight || (st.weight === allTimePR.weight && st.reps > allTimePR.reps)) {
                  allTimePR = { weight: st.weight, reps: st.reps };
                }
              }
            }
          }

          // Skipped
          if (skipped && isToday) {
            return (
              <div key={ex.id} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 16px", borderRadius: 14, marginBottom: 6,
                background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)",
              }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-3)" }}>{ex.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", opacity: 0.6 }}>{ex.sets}x{repLabel} · Skipped</div>
                </div>
                <button onClick={() => onSkip(ex.id, true)} style={{
                  fontSize: 11, padding: "6px 12px", borderRadius: 7, fontWeight: 700, fontFamily: "inherit",
                  background: "rgba(248,113,113,0.08)", color: "var(--red)",
                  border: "1px solid rgba(248,113,113,0.2)", cursor: "pointer", flexShrink: 0,
                }}>Undo</button>
              </div>
            );
          }

          return (
            <button key={ex.id}
              onClick={() => isToday && onStartExercise(displayEx)}
              disabled={!isToday}
              style={{
                width: "100%", textAlign: "left", padding: "14px 16px", borderRadius: 14, marginBottom: 6,
                cursor: isToday ? "pointer" : "default", fontFamily: "inherit", border: "none",
                background: done ? "linear-gradient(145deg, rgba(52,211,153,0.06), rgba(52,211,153,0.02))" : isSwapped ? "linear-gradient(145deg, #1a1a2e, #0f0f23)" : "linear-gradient(145deg, #111b2e, #0d1526)",
                outline: hasPR ? "1px solid rgba(251,191,36,0.2)" : done ? "1px solid rgba(52,211,153,0.1)" : isSwapped ? `1px solid ${dayPlan.color}30` : "1px solid rgba(255,255,255,0.03)",
              }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: done ? "var(--green)" : "var(--text-1)" }}>{displayEx.name}</span>
                    {done && <span style={{ color: "var(--green)", fontSize: 13 }}>✓</span>}
                    {hasPR && <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 5px", borderRadius: 4, background: "var(--amber-dim)", color: "var(--amber)" }}>PR</span>}
                    {(() => {
                      const dbEx = EXERCISE_DATABASE.find(e => e.id === displayEx.id)
                                || EXERCISE_DATABASE.find(e => e.name === displayEx.name);
                      const vid = dbEx?.videoId || displayEx.videoId;
                      return vid ? (
                        <button onClick={e => { e.stopPropagation(); setVideoModalFor({ videoId: vid, name: displayEx.name }); }} style={{
                          width: 20, height: 20, borderRadius: "50%", border: "none", cursor: "pointer", padding: 0,
                          background: `${dayPlan.color}22`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                          <svg viewBox="0 0 24 24" width="10" height="10" fill={dayPlan.color}><path d="M8 5v14l11-7z"/></svg>
                        </button>
                      ) : null;
                    })()}
                  </div>
                  {/* Swap indicator pill */}
                  {isSwapped && (
                    <div style={{ marginBottom: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: `${dayPlan.color}18`, color: dayPlan.color }}>
                        ⇄ swapped from {ex.name}
                      </span>
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: dayPlan.color, opacity: 0.7 }}>{displayEx.sets}x{isSwapped ? `${displayEx.rLo}-${displayEx.rHi}` : repLabel}</span>
                    {lastW.length > 0 && <span style={{ fontSize: 11, color: "var(--text-3)" }}>Prev: {lastW.map(s => `${s.weight}x${s.reps}`).join("  ")}</span>}
                  </div>
                  {sug && !done && isToday && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                      <span style={{
                        fontSize: 10, padding: "2px 7px", borderRadius: 5, fontWeight: 800,
                        background: sug.action === "up" ? "var(--green-dim)" : sug.action === "down" ? "var(--red-dim)" : "var(--bg-3)",
                        color: sug.action === "up" ? "var(--green)" : sug.action === "down" ? "var(--red)" : "var(--text-2)",
                      }}>
                        {sug.action === "up" ? "↑ GO UP" : sug.action === "down" ? "↓ HOLD" : "→ SAME"}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: sug.action === "up" ? "var(--green)" : sug.action === "down" ? "var(--red)" : "var(--text-2)" }}>
                        {sug.msg}
                      </span>
                    </div>
                  )}
                  {ws.length > 0 && !done && <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 3 }}>Today: {ws.map(s => `${s.weight}x${s.reps}`).join("  ")}</div>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, marginLeft: 8, flexShrink: 0 }}>
                  {allTimePR && !isSwapped && (
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: "var(--amber)", letterSpacing: 0.5 }}>ALL-TIME PR</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-1)" }}>
                        {allTimePR.weight}<span style={{ fontSize: 10, color: "var(--text-3)", fontWeight: 600 }}> lbs</span>
                        <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 600 }}> ×{allTimePR.reps}</span>
                      </div>
                    </div>
                  )}
                  {isToday && !done && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {isSwapped ? (
                        <button onClick={e => { e.stopPropagation(); onRestoreSwap(ex.id); }} style={{
                          fontSize: 11, padding: "6px 12px", borderRadius: 7, fontWeight: 700, fontFamily: "inherit",
                          background: `${dayPlan.color}15`, color: dayPlan.color,
                          border: `1px solid ${dayPlan.color}30`, cursor: "pointer",
                        }}>Restore</button>
                      ) : (
                        <>
                          <button onClick={e => { e.stopPropagation(); setSwapPickerFor({ exId: ex.id, exName: ex.name }); }} style={{
                            fontSize: 11, padding: "6px 12px", borderRadius: 7, fontWeight: 700, fontFamily: "inherit",
                            background: "rgba(255,255,255,0.05)", color: "var(--text-2)",
                            border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer",
                          }}>⇄ Swap</button>
                          <button onClick={e => { e.stopPropagation(); onSkip(ex.id, false); }} style={{
                            fontSize: 11, padding: "6px 12px", borderRadius: 7, fontWeight: 700, fontFamily: "inherit",
                            background: "rgba(255,255,255,0.05)", color: "var(--text-2)",
                            border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer",
                          }}>Skip</button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 3, marginTop: 8 }}>
                {Array.from({ length: ex.sets }).map((_, i) => (
                  <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < ws.length ? (done ? "var(--green)" : dayPlan.color) : "rgba(255,255,255,0.05)", transition: "all 0.3s" }} />
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {isToday && (
        <div style={{ padding: "8px 20px 0" }}>
          <textarea value={notes} onChange={e => { setNotes(e.target.value); onSaveNotes(e.target.value); }}
            placeholder="Session notes..." rows={2}
            style={{ ...s.inp, fontSize: 14, resize: "none", padding: "10px 14px" }} />
        </div>
      )}

      {/* CARDIO SECTION */}
      {isToday && (
        <div style={{ padding: "10px 20px 0" }}>
          {/* Logged entries */}
          {cardioEntries.map((entry, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 14px", borderRadius: 12, marginBottom: 6,
              background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.12)",
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--green)" }}>
                  {entry.type} · {entry.duration} min
                </div>
                {entry.type === "Treadmill" && (
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                    {entry.incline}% incline · {entry.speed} mph
                  </div>
                )}
              </div>
              <button onClick={() => onRemoveCardio(i)} style={{
                width: 28, height: 28, borderRadius: 7, border: "none",
                background: "rgba(248,113,113,0.08)", color: "var(--red)", fontSize: 13, cursor: "pointer",
              }}>✕</button>
            </div>
          ))}

          {/* Add form or button */}
          {!showCardioForm ? (
            <button onClick={() => setShowCardioForm(true)} style={{
              width: "100%", padding: "13px 0", borderRadius: 14, fontSize: 14, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit",
              background: "rgba(52,211,153,0.06)", color: "var(--green)",
              border: "1px dashed rgba(52,211,153,0.2)",
            }}>
              {cardioEntries.length > 0 ? "+ Add More Cardio" : "+ Log Cardio"}
            </button>
          ) : (
            <div style={{ ...s.card, padding: 16, border: "1px solid rgba(52,211,153,0.1)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ ...s.label, marginBottom: 0 }}>CARDIO</div>
                <button onClick={() => setShowCardioForm(false)} style={{ fontSize: 11, color: "var(--text-3)", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                {["Treadmill", "Bike", "Run", "Walk", "Other"].map(t => (
                  <button key={t} onClick={() => setCardio(c => ({ ...c, type: t }))} style={{
                    padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit",
                    background: cardio.type === t ? "rgba(52,211,153,0.15)" : "var(--bg-2)",
                    color: cardio.type === t ? "var(--green)" : "var(--text-3)",
                  }}>{t}</button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: cardio.type === "Treadmill" ? 10 : 0 }}>
                <div style={{ flex: 1 }}>
                  <div style={s.label}>DURATION (min)</div>
                  <input type="number" inputMode="numeric" value={cardio.duration}
                    onChange={e => setCardio(c => ({ ...c, duration: e.target.value }))}
                    style={{ ...s.inp, textAlign: "center", fontSize: 18, padding: "8px 0" }} />
                </div>
                {cardio.type === "Treadmill" && (
                  <>
                    <div style={{ flex: 1 }}>
                      <div style={s.label}>INCLINE (%)</div>
                      <input type="number" inputMode="decimal" value={cardio.incline}
                        onChange={e => setCardio(c => ({ ...c, incline: e.target.value }))}
                        style={{ ...s.inp, textAlign: "center", fontSize: 18, padding: "8px 0" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={s.label}>SPEED (mph)</div>
                      <input type="number" inputMode="decimal" value={cardio.speed}
                        onChange={e => setCardio(c => ({ ...c, speed: e.target.value }))}
                        style={{ ...s.inp, textAlign: "center", fontSize: 18, padding: "8px 0" }} />
                    </div>
                  </>
                )}
              </div>
              <button onClick={() => {
                onSaveCardio(cardio);
                setShowCardioForm(false);
                setCardioSaved(true);
                setCardio({ type: "Treadmill", duration: 30, incline: 12, speed: 3.5, notes: "" });
                setTimeout(() => setCardioSaved(false), 2000);
              }} style={{
                ...s.btnP, width: "100%", height: 44, fontSize: 14, fontFamily: "inherit", marginTop: 10,
                background: cardioSaved ? "linear-gradient(135deg, #34d399, #059669)" : s.btnP.background,
              }}>
                {cardioSaved ? "✓ Saved" : "Save Cardio"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Swap exercise picker */}
      {swapPickerFor && (
        <ExercisePicker
          dayColor={dayPlan.color}
          onClose={() => setSwapPickerFor(null)}
          onSelect={replacementEx => {
            onSwap(swapPickerFor.exId, swapPickerFor.exName, replacementEx);
            setSwapPickerFor(null);
          }}
        />
      )}

      {/* Video modal from workout card play button */}
      {videoModalFor && (
        <VideoModal
          videoId={videoModalFor.videoId}
          exerciseName={videoModalFor.name}
          dayColor={dayPlan.color}
          onClose={() => setVideoModalFor(null)}
        />
      )}
    </div>
  );
}
